import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Typography,
  Divider,
} from '@mui/material';
import { useParams, useLocation , useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { postRequest } from '../../../lib/api'; 

const rows = [
  { complexity: 'Low', rangeLabel: '< £325,000' },
  { complexity: 'Medium', rangeLabel: '£325,000 - £650,000' },
  { complexity: 'High', rangeLabel: '£650,000 +' },
];

const slugToTitle = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const VAT_RATE = 0.2;

const LssGrantApplication = () => {
  const { title } = useParams();
  const location = useLocation()
  const product_id = location.state.product_id;
  const partner_id = location.state.partner_id;
  const navigate = useNavigate();
  
  const readableTitle = slugToTitle(title || '');

  const [costInputs, setCostInputs] = useState([
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
  ]);

  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const fetchGrantApplicationCosts = async () => {
      try {
        const response = await postRequest('private/getPartnerGrantApplicationRateCard', {
          product_id,
          partner_id
        });

        if (response.data) {
          setEditId(response.data._id);

          if (response.data.costBreakdowns) {
            const updatedCosts = response.data.costBreakdowns.map(cost => ({
              mlmLegalCost: cost.mlmLegalCost.toString(),
              vat: cost.vat,
              totalCost: cost.totalCost,
            }));
            setCostInputs(updatedCosts);
          }
        }
      } catch (error) {
        console.error('Error fetching partner LSS grant application costs:', error);
      }
    };

    if (product_id && partner_id) {
      fetchGrantApplicationCosts();
    }
  }, [product_id, partner_id]);

  const handleChange = (index, value) => {
    const cost = parseFloat(value) || 0;
    const vat = parseFloat((cost * VAT_RATE).toFixed(2));
    const total = parseFloat((cost + vat).toFixed(2));

    const updated = [...costInputs];
    updated[index] = {
      mlmLegalCost: value,
      vat,
      totalCost: total,
    };
    setCostInputs(updated);
  };

  const handleSubmit = async () => {
    const costBreakdowns = rows.map((row, index) => ({
      complexity: row.complexity,
      rangeLabel: row.rangeLabel,
      mlmLegalCost: parseFloat(costInputs[index].mlmLegalCost) || 0,
      vat: costInputs[index].vat,
      totalCost: costInputs[index].totalCost,
    }));

    try {
      // Partner rate cards use create endpoint for both create and update
      await postRequest('private/createPartnerGrantApplicationRateCard', {
        product_id,
        partner_id,
        costBreakdowns,
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Partner LSS Grant Application rate card saved successfully!',
      });
      navigate(-1);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.message || 'Something went wrong',
      });
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h5" fontWeight="600" mb={2}>
        Manage {readableTitle}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
              <TableCell><strong>Complexity</strong></TableCell>
              <TableCell><strong>Range</strong></TableCell>
              <TableCell><strong>MLM Legal Cost</strong></TableCell>
              <TableCell><strong>VAT</strong></TableCell>
              <TableCell><strong>Total Cost</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.complexity}</TableCell>
                <TableCell>{row.rangeLabel}</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="£"
                    fullWidth
                    variant="outlined"
                    value={costInputs[idx].mlmLegalCost}
                    onChange={(e) => handleChange(idx, e.target.value)}
                  />
                </TableCell>
                <TableCell>£{costInputs[idx].vat.toFixed(2)}</TableCell>
                <TableCell>£{costInputs[idx].totalCost.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={3} display="flex" justifyContent="flex-start" gap={2}>
        <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          className="red-bg red-button text-capitalize"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default LssGrantApplication;
